import { useState, useEffect } from 'react';
import api from '../api/client';
import VersionCompareModal from './VersionCompareModal';
import { STATUS_LABEL, won, amountDelta } from '../utils/versionUtils';

// 버전 정렬/최신/현재 계산 (버전이 2개 미만이면 null)
function versionState(versions, currentId) {
  if (!currentId || !versions || versions.length < 2) return null;
  const cur = parseInt(currentId);
  const sorted = [...versions].sort((a, b) => a.version - b.version);
  return { cur, sorted, latest: sorted[sorted.length - 1] };
}

// 작성 화면 상단바(견적번호 오른쪽)에 들어가는 버전 라벨 — v1 v2 v3 (현재 버전 강조, 클릭하면 이동)
export function VersionTabs({ versions, currentId, basePath }) {
  const st = versionState(versions, currentId);
  if (!st) return null;
  const { cur, sorted, latest } = st;
  const go = (id) => { if (id !== cur) window.location.assign(`${basePath}?id=${id}`); };
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, flexWrap:'wrap' }}>
      {sorted.map((v, i) => {
        const active = v.id === cur;
        const prev = sorted[i - 1];
        const delta = prev ? amountDelta(v.total_amount, prev.total_amount) : null;
        return (
          <span key={v.id} onClick={() => go(v.id)}
            title={`${STATUS_LABEL[v.status] || v.status} · ${won(v.total_amount)}${delta ? ` · 직전 대비 ${delta.text}` : ''}${v.version_note ? `\n“${v.version_note}”` : ''}`}
            style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 10px', borderRadius:14, fontSize:12, fontWeight:active ? 700 : 500,
              cursor:active ? 'default' : 'pointer', border:`1px solid ${active ? '#3b82f6' : '#e2e8f0'}`,
              background:active ? '#3b82f6' : '#fff', color:active ? '#fff' : '#475569' }}>
            v{v.version}{v.id === latest.id && <span style={{ fontSize:9, opacity:0.8 }}>최신</span>}
          </span>
        );
      })}
    </span>
  );
}

// 이전 버전을 보고 있을 때만 나오는 안내 배너 (최신 버전이면 아무것도 그리지 않음 → 평소에는 공간을 차지하지 않음)
export function OldVersionBanner({ versions, currentId, basePath }) {
  const st = versionState(versions, currentId);
  if (!st || st.latest.id === st.cur) return null;
  const { cur, sorted, latest } = st;
  return (
    <div style={{ marginBottom:12, padding:'8px 14px', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, fontSize:12, color:'#92400e', display:'flex', alignItems:'center', gap:10 }}>
      <span>⚠ 이전 버전(v{sorted.find(v => v.id === cur)?.version})을 보고 있습니다. 최신 버전은 v{latest.version} 입니다.</span>
      <button className="btn btn-outline btn-sm" style={{ marginLeft:'auto' }} onClick={() => window.location.assign(`${basePath}?id=${latest.id}`)}>최신 버전(v{latest.version})으로 이동</button>
    </div>
  );
}

// 버전 이력 카드 상단: 현재 버전 메모 편집 + 다른 버전과 비교
export function VersionMemoCompare({ versions, currentId, canEditNote, onNoteSaved }) {
  const cur = versions.find(v => v.id === parseInt(currentId));
  const [note, setNote] = useState(cur?.version_note || '');
  const [saving, setSaving] = useState(false);
  const [cmpWith, setCmpWith] = useState('');
  const [modal, setModal] = useState(null);
  useEffect(() => { setNote(cur?.version_note || ''); }, [cur?.id, cur?.version_note]);
  if (!cur) return null;
  const others = versions.filter(v => v.id !== cur.id).sort((a, b) => b.version - a.version);
  const dirty = (note.trim() || '') !== (cur.version_note || '');

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/estimates/${cur.id}/version-note`, { version_note: note });
      onNoteSaved(cur.id, r.version_note || null);
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end', marginBottom:12, paddingBottom:12, borderBottom:'1px solid #f1f5f9' }}>
      <div style={{ flex:'1 1 320px' }}>
        <label className="form-label" style={{ marginBottom:3 }}>v{cur.version} 버전 메모 <span style={{ color:'#94a3b8', fontWeight:400 }}>(이 버전을 만든 사유 · 목록에도 표시됩니다)</span></label>
        <div style={{ display:'flex', gap:6 }}>
          <input className="form-input" style={{ flex:1 }} maxLength={300} value={note} disabled={!canEditNote}
            onChange={e => setNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && dirty && canEditNote) save(); }}
            placeholder={canEditNote ? '예) 고객 요청으로 라이선스 수량 조정' : '메모 없음'} />
          {canEditNote && <button className="btn btn-outline btn-sm" disabled={!dirty || saving} onClick={save}>{saving ? '저장 중...' : '메모 저장'}</button>}
        </div>
      </div>
      {others.length > 0 && (
        <div>
          <label className="form-label" style={{ marginBottom:3 }}>버전 비교</label>
          <div style={{ display:'flex', gap:6 }}>
            <select className="form-input" style={{ width:200 }} value={cmpWith} onChange={e => setCmpWith(e.target.value)}>
              <option value="">v{cur.version} 와 비교할 버전...</option>
              {others.map(v => <option key={v.id} value={v.id}>v{v.version} · {STATUS_LABEL[v.status] || v.status} · {won(v.total_amount)}</option>)}
            </select>
            <button className="btn btn-outline btn-sm" disabled={!cmpWith} onClick={() => setModal({ a: parseInt(cmpWith), b: cur.id })}>⇄ 비교</button>
          </div>
        </div>
      )}
      {modal && <VersionCompareModal aId={modal.a} bId={modal.b} onClose={() => setModal(null)} />}
    </div>
  );
}
